// src/pages/PromotionsPage.js
import React, { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import DashboardLayout from "../../components/test/DashboardLayout";
import PromotionsFilterBar from "../../components/promotions/PromotionsFilterBar";
import PromotionsTable from "../../components/promotions/PromotionsTable";
import PromotionFormModal from "../../components/modals/PromotionFormModal";
import {useParams} from "react-router-dom";

const PromotionsPage = () => {
    const {userId, businessId} = useParams();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPromotion, setSelectedPromotion] = useState(null); // Null for 'create', object for 'edit'
    const [filterStatus, setFilterStatus] = useState('All');
    const [filterType, setFilterType] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');

    // Mock Promotions Data
    const [promotions, setPromotions] = useState([
        {
            id: 'promo-001',
            name: 'Double Points Weekend',
            type: 'Bonus Points',
            target: 'All Members',
            status: 'Active',
            startDate: '2025-06-07',
            endDate: '2025-06-09',
            details: 'Earn 2x points on all purchases this weekend!',
            usage: 150,
            redemptions: 0 // Not directly redeemable, points are auto-applied
        },
        {
            id: 'promo-002',
            name: 'ZAR100 Off Next Purchase',
            type: 'Discount Code',
            target: 'Gold Tier Members',
            status: 'Active',
            startDate: '2025-06-01',
            endDate: '2025-06-30',
            details: 'Use code "SAVE10" at checkout. Min spend $50.',
            usage: 80,
            redemptions: 45
        },
        {
            id: 'promo-003',
            name: 'Welcome Bonus Points',
            type: 'Bonus Points',
            target: 'New Sign-ups',
            status: 'Active',
            startDate: '2025-01-01',
            endDate: 'Ongoing',
            details: 'Get 100 bonus points on first sign-up.',
            usage: 300,
            redemptions: 0 // Auto-applied
        },
        {
            id: 'promo-004',
            name: 'Free Coffee with 200 pts',
            type: 'Special Offer',
            target: 'All Members',
            status: 'Inactive',
            startDate: '2024-11-01',
            endDate: '2024-12-31',
            details: 'Redeem 200 points for a free standard coffee.',
            usage: 200,
            redemptions: 180
        },
        {
            id: 'promo-005',
            name: 'Birthday Month Treat',
            type: 'Discount Code',
            target: 'Birthday Members',
            status: 'Active',
            startDate: '2025-01-01',
            endDate: 'Ongoing',
            details: '20% off during your birthday month. Unique code sent via email.',
            usage: 65,
            redemptions: 30
        },
    ]);

    const availableStatuses = ['All', 'Active', 'Inactive', 'Upcoming', 'Expired'];
    const availableTypes = ['All', 'Discount Code', 'Bonus Points', 'Special Offer'];
    const availableTargets = ['All Members', 'New Sign-ups', 'Gold Tier Members', 'Silver Tier Members', 'Bronze Tier Members', 'Birthday Members'];


    const filteredPromotions = promotions.filter(promo => {
        const matchesSearch = promo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            promo.details.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'All' || promo.status === filterStatus;
        const matchesType = filterType === 'All' || promo.type === filterType;
        return matchesSearch && matchesStatus && matchesType;
    });

    const handleCreatePromotion = () => {
        setSelectedPromotion(null); // Ensure no promotion is selected for creation mode
        setIsModalOpen(true);
    };

    const handleEditPromotion = (promotion) => {
        setSelectedPromotion(promotion);
        setIsModalOpen(true);
    };

    const handleSavePromotion = (promoData) => {
        if (promoData.id) {
            // Update existing promotion
            setPromotions(promotions.map(promo => (promo.id === promoData.id ? promoData : promo)));
        } else {
            // Add new promotion
            const newPromotion = {
                ...promoData,
                id: `promo-${Date.now()}`, // Simple unique ID
                usage: 0,
                redemptions: 0,
                status: 'Upcoming' // Default status for new promotions
            };
            setPromotions([...promotions, newPromotion]);
        }
        setIsModalOpen(false);
        setSelectedPromotion(null);
    };

    const handleChangePromotionStatus = (id, newStatus) => {
        setPromotions(promotions.map(promo =>
            promo.id === id ? { ...promo, status: newStatus } : promo
        ));
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPromotion(null);
    };

    return (
        <DashboardLayout userId={userId} businessId={businessId}>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Promotions</h1>
                <button
                    onClick={handleCreatePromotion}
                    className="bg-indigo-600 text-white px-5 py-2 rounded-lg flex items-center space-x-2 hover:bg-indigo-700 transition duration-200 shadow-md"
                >
                    <PlusIcon className="h-5 w-5" />
                    <span>Create New Promotion</span>
                </button>
            </div>

            <PromotionsFilterBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                availableStatuses={availableStatuses}
                filterType={filterType}
                setFilterType={setFilterType}
                availableTypes={availableTypes}
            />

            {promotions.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-8 text-center border-dashed border-2 border-gray-300 mt-6">
                    <p className="text-xl font-semibold text-gray-700 mb-4">No promotions created yet!</p>
                    <p className="text-gray-500 mb-6">Start by creating your first promotional campaign to engage your loyalty members.</p>
                    <button
                        onClick={handleCreatePromotion}
                        className="bg-indigo-600 text-white px-6 py-3 rounded-lg flex items-center space-x-2 mx-auto hover:bg-indigo-700 transition duration-200 shadow-lg"
                    >
                        <PlusIcon className="h-6 w-6" />
                        <span>Create Your First Promotion</span>
                    </button>
                </div>
            ) : (
                <PromotionsTable
                    promotions={filteredPromotions}
                    onEditPromotion={handleEditPromotion}
                    onChangeStatus={handleChangePromotionStatus}
                />
            )}

            <PromotionFormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                promotion={selectedPromotion} // Will be null for creation
                onSave={handleSavePromotion}
                availableTypes={availableTypes.filter(type => type !== 'All')}
                availableTargets={availableTargets}
            />
        </DashboardLayout>
    );
};

export default PromotionsPage;